// Laser kiosk scans (2026-10-30): the calls and rules behind components/laserKiosk/KioskScanner.jsx.
// API: jobq-api "Laser kiosk scans". Tests: kioskScan.test.js.
import { createContext } from "react";
import config from "../../config";
import { doNotAddReason } from "./kioskRules";

/** scanOn = the API's switch; viewingLive = the kiosk shows the job on the laser (a scan is judged against that job). */
export const KioskScanContext = createContext({ scanOn: false, viewingLive: true });

/**
 * How the kiosk tells a scan from a person. Any text counts (the API says what it is, and logs it), as short as a
 * 3-character run, because a scan's Enter must never reach a focused button or PIN prompt. A person types 100+ ms
 * apart. A bare code gets the same 60 ms as a link: the 35 ms default was measured on a Mac, and the Pi is busier.
 */
export const KIOSK_DETECTOR = { minChars: 3, bareMaxGapMs: 60, joinHiccupMs: 1000 };

export function kioskParse(text) {
  const code = String(text == null ? "" : text).trim();
  return code ? { code } : null;
}

/** How long a result stays up, in ms. null = until the next scan or OK: a wrong pallet is never missed. */
export function resultMs(tone) {
  if (tone === "bad") return null;
  return tone === "warn" ? 8000 : 5000;
}

/** "need" (ask for a scan), "done", or null (nothing to say about this destination). */
export function palletScanState(dest, { scanOn, viewingLive }) {
  if (!scanOn || !dest || dest.kind !== "packaging" || dest.scan_pallet_id == null) return null;
  if (dest.pallet_scan) return "done";
  if (!viewingLive || !dest.pallet_beacon_id || doNotAddReason(dest)) return null;
  return "need";
}

/** How many pallets the strip lists before "+N more": the kiosk has no page scroll. */
export const STRIP_MAX_ROWS = 4;

/** The pallets still to scan on the live job, once each, in the order the parts list them. */
export function palletsToScan(parts, scanOn) {
  const out = new Map();
  (parts || []).forEach((part) => (part.destinations || []).forEach((dest) => {
    if (palletScanState(dest, { scanOn, viewingLive: true }) === "need" && !out.has(dest.scan_pallet_id)) {
      out.set(dest.scan_pallet_id, {
        pallet_id: dest.scan_pallet_id, short_id: dest.scan_pallet_short_id, beacon_id: dest.pallet_beacon_id,
        so_number: dest.dest_so_number,
      });
    }
  }));
  return Array.from(out.values());
}

async function post(path, body) {
  const res = await fetch(`${config.API_BASE_URL}${path}`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
  const out = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = out && out.detail;
    throw new Error((detail && detail.message) || (typeof detail === "string" && detail) || `HTTP ${res.status}`);
  }
  return out;
}

export function sendScan(code) {
  return post("/laser/scan", { code });
}

export function confirmPalletNoScan(palletId, pin) {
  return post("/laser/pallet_confirm", { pallet_id: palletId, pin });
}

/** Stop the pallet's flashing: the operator has it. A light that won't stop runs out by itself, so a failure is ignored. */
export function stopPalletLight(taskId, palletId) {
  return post(`/tasks/${taskId}/pallet_beacons/lights`, { action: "off", pallet_ids: [palletId], actor: "laser scan" })
    .catch(() => null);
}
